import { useCallback, useMemo } from 'react'
import { Ban, Check, Star } from 'lucide-react'
import { PageHeader } from '@/shell/AppShell'
import { DataTable } from '@/components/DataTable'
import type { AdminColumn, RowAction } from '@/components/DataTable/types'
import { PERMISSIONS } from '@/lib/permissions'
import { useToast } from '@/providers/UiProviders'
import {
  fetchPaymentFraud,
  resolvePaymentFraud,
  FRAUD_STR as S,
  type PaymentFraudRow,
} from './fraudApi'

/**
 * A25.13 — Мошенничество со Stars. Suspected fraudulent Stars/payment events
 * from the REAL `/admin/antifraud/payments` feed; confirm (freeze/charge back)
 * or clear. Placeholder permission USERS_VIEW.
 */
export function StarsFraudScreen() {
  const { toast } = useToast()

  const columns = useMemo<AdminColumn<PaymentFraudRow>[]>(
    () => [
      { key: 'user', header: S.user, type: 'text' },
      { key: 'amount', header: S.stars, type: 'number' },
      { key: 'method', header: S.method, type: 'text' },
      { key: 'reason', header: S.reason, type: 'text' },
      {
        key: 'status',
        header: S.status,
        type: 'enum',
        options: [
          { value: 'open', label: S.openCount, status: 'open' },
          { value: 'confirmed', label: S.confirmed, status: 'banned' },
          { value: 'cleared', label: S.cleared, status: 'active' },
        ],
      },
      { key: 'created_at', header: S.created, type: 'text' },
    ],
    [],
  )

  const resolve = useCallback(
    async (row: PaymentFraudRow, action: 'confirm' | 'clear') => {
      try {
        await resolvePaymentFraud(row.id, action)
        toast(action === 'confirm' ? S.confirmed : S.cleared, { tone: 'success' })
      } catch {
        toast(S.failed, { tone: 'error' })
      }
    },
    [toast],
  )

  const rowActions = useMemo<RowAction<PaymentFraudRow>[]>(
    () => [
      {
        id: 'confirm',
        label: S.confirm,
        icon: <Ban size={14} />,
        danger: true,
        visible: (r) => r.status === 'open',
        onSelect: (r) => resolve(r, 'confirm'),
      },
      {
        id: 'clear',
        label: S.clear,
        icon: <Check size={14} />,
        visible: (r) => r.status === 'open',
        onSelect: (r) => resolve(r, 'clear'),
      },
    ],
    [resolve],
  )

  const fetcher = useCallback(fetchPaymentFraud, [])
  return (
    <>
      <PageHeader title={S.starsFraud} screenId="A25.13" />
      <DataTable<PaymentFraudRow>
        screenId="A25.13"
        permission={PERMISSIONS.USERS_VIEW}
        columns={columns}
        fetcher={fetcher}
        rowId={(r) => String(r.id)}
        rowActions={rowActions}
        emptyState={{ icon: <Star size={28} strokeWidth={1.5} /> }}
      />
    </>
  )
}
